// The four guide themes the blog hub (/blog) branches into. Each theme has its
// own listing page at /blog/<id> listing its posts. Theme ids share
// that URL space with post slugs (and win on a clash), so never reuse a post slug.
// Each post belongs to exactly one theme, set in blog-posts.ts.
import lamperImage from "@/assets/farve/coll-ceiling.jpg";
import materialerImage from "@/assets/farve/vase-craft.jpg";
import vintageImage from "@/assets/farve/all-goods.jpg";

export type BlogThemeId =
  | "lamper-og-belysning"
  | "materialer-og-haandvaerk"
  | "vintage-og-design"
  | "hjem-og-indretning";

type ThemeCopy = {
  /** Full name, used as card title and H1 */
  title: string;
  /** Short label for eyebrows on cards and posts */
  label: string;
  /** One line under the card title on the hub */
  teaser: string;
  /** Intro under the H1 on the theme page */
  intro: string;
  metaTitle: string;
  metaDescription: string;
};

export type BlogTheme = {
  id: BlogThemeId;
  image: string;
  imageAlt: { da: string; en: string };
  da: ThemeCopy;
  en: ThemeCopy;
};

// Photo from the "Dreamy sofabord" DIY post.
const HJEM_IMAGE =
  "https://cdn.shopify.com/s/files/1/0562/8516/7668/articles/62B83C15-87C9-42D1-AFEA-17A0EFA1987D_1600x.jpg?v=1647893611";

export const blogThemes: BlogTheme[] = [
  {
    id: "lamper-og-belysning",
    image: lamperImage,
    imageAlt: {
      da: "Tændt hvid Murano loftlampe med swirl-mønster på en træstol foran et lyserødt gardin",
      en: "Lit white Murano ceiling lamp with a swirl pattern on a wooden chair in front of a pink curtain",
    },
    da: {
      title: "Lamper & belysning",
      label: "Belysning",
      teaser: "Find den rigtige lampe, og få mere stemning ud af lyset i dit hjem.",
      intro:
        "Guides til vintage lamper og lys i hjemmet — fra Murano-lamper og mushroom lamper til hvordan du vælger, placerer og passer på dem.",
      metaTitle: "Lamper & belysning — guides | Farveladeland",
      metaDescription:
        "Guides til vintage lamper og belysning: Murano-lamper, mushroom lamper, væglamper og loftlamper — og hvordan du vælger den rigtige til dit hjem.",
    },
    en: {
      title: "Lamps & lighting",
      label: "Lighting",
      teaser: "Find the right lamp and get more atmosphere out of the light at home.",
      intro:
        "Guides to vintage lamps and light at home — from Murano and mushroom lamps to choosing, placing and caring for them.",
      metaTitle: "Lamps & lighting — guides | Farveladeland",
      metaDescription:
        "Guides to vintage lamps and lighting: Murano lamps, mushroom lamps, wall and ceiling lamps — and how to pick the right one for your home.",
    },
  },
  {
    id: "materialer-og-haandvaerk",
    image: materialerImage,
    imageAlt: {
      da: "Hvid Murano vase med snoede tråde i lyserød, grøn og blå",
      en: "White Murano vase with twisted threads in pink, green and blue",
    },
    da: {
      title: "Materialer & håndværk",
      label: "Håndværk",
      teaser: "Forstå glasset, keramikken og håndværket bag dine fund — og pas godt på dem.",
      intro:
        "Guides til de materialer og det håndværk, vi møder i vintagefundene — mundblæst glas, keramik og service — og hvordan du passer på dem, så de holder i mange år endnu.",
      metaTitle: "Materialer & håndværk — guides | Farveladeland",
      metaDescription:
        "Bliv klogere på mundblæst Murano-glas, keramik og vintage service — og lær, hvordan du vasker og passer på dine gamle skatte.",
    },
    en: {
      title: "Materials & craft",
      label: "Craft",
      teaser: "Understand the glass, ceramics and craft behind your finds — and look after them.",
      intro:
        "Guides to the materials and craft we meet in vintage finds — hand-blown glass, ceramics and tableware — and how to care for them so they last for years to come.",
      metaTitle: "Materials & craft — guides | Farveladeland",
      metaDescription:
        "Learn about hand-blown Murano glass, ceramics and vintage tableware — and how to wash and care for your old treasures.",
    },
  },
  {
    id: "vintage-og-design",
    image: vintageImage,
    imageAlt: {
      da: "Farverige vintagefund i glas og keramik lagt ud på et hvidt lagen",
      en: "Colourful vintage finds in glass and ceramics laid out on a white sheet",
    },
    da: {
      title: "Vintage & design",
      label: "Vintage",
      teaser: "Lær at kende forskel på ægte vintage og nyt — og bliv klogere på designet.",
      intro:
        "Guides til vintage og design — hvordan du kender de ægte fund, hvad der adskiller gammelt fra nyproduceret, og historierne bag de ting, vi finder.",
      metaTitle: "Vintage & design — guides | Farveladeland",
      metaDescription:
        "Guides til vintage og design: kend forskel på ægte vintage og nyproduceret, og bliv klogere på historien bag Murano-glas og andre fund.",
    },
    en: {
      title: "Vintage & design",
      label: "Vintage",
      teaser: "Learn to tell real vintage from new — and get to know the design.",
      intro:
        "Guides to vintage and design — how to spot the real finds, what separates old from newly made, and the stories behind the things we find.",
      metaTitle: "Vintage & design — guides | Farveladeland",
      metaDescription:
        "Guides to vintage and design: tell genuine vintage from new, and learn the story behind Murano glass and other finds.",
    },
  },
  {
    id: "hjem-og-indretning",
    image: HJEM_IMAGE,
    imageAlt: {
      da: "Lilla sofabord i skyform med planter og vintagefund i en stue",
      en: "Lilac cloud-shaped coffee table with plants and vintage finds in a living room",
    },
    da: {
      title: "Hjem & indretning",
      label: "Hjemme",
      teaser: "Idéer, DIY og små projekter, der giver hjemmet mere farve og personlighed.",
      intro:
        "Idéer og projekter fra vores eget hjem — DIY, små opgraderinger og indretning med farve, vintagefund og plads til personlighed.",
      metaTitle: "Hjem & indretning — guides | Farveladeland",
      metaDescription:
        "Indretningsidéer, DIY og små projekter på budget — med farve, vintagefund og personlighed. Guides fra Farveladeland.",
    },
    en: {
      title: "Home & interiors",
      label: "Home",
      teaser: "Ideas, DIY and small projects that bring more colour and personality home.",
      intro:
        "Ideas and projects from our own home — DIY, small upgrades and interiors with colour, vintage finds and room for personality.",
      metaTitle: "Home & interiors — guides | Farveladeland",
      metaDescription:
        "Interior ideas, DIY and small projects on a budget — with colour, vintage finds and personality. Guides from Farveladeland.",
    },
  },
];

export function getBlogTheme(id: string): BlogTheme | undefined {
  return blogThemes.find((t) => t.id === id);
}
